import { boardService } from '../../../services/board.service.js';
import { imageService } from '../../../services/image.service.js';
import { taskService } from '../../../services/task.service.js';
import { deleteCommentReplyFiles, uploadService } from '../../../services/upload.service.js';
import { utilService } from '../../../services/util.service.js';
import { setBoard, setFilterBoard } from '../../board/reducers/board.reducer.js';
import { dispatchMultipleActions } from '../../dispatchHelper.js';
import { updateGroup } from '../../group/reducers/group.reducer.js';
import { store } from '../../store.js';
import { addTask, clearCommentDraft, clearGroupTasks, removeGroupTask, setCommentDraft, setCurrentSubtask, setGroupTasks, setIsSubtask, setSubtasks, updateGroupTask, updateTask } from '../../task/reducers/task.reducer.js';
import { setLoading } from '../../user/reducers/auth.reducer.js';
import { socket } from '../../../socket.listener/socket';

const dispatch = store.dispatch

function stableStringify(value) {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(',')}}`;
  }

  return JSON.stringify(value ?? null);
}

function getTaskFilterSignature(filters = {}) {
  return stableStringify({
    searchTitle: String(filters.searchTitle || filters.title || '').trim().toLowerCase(),
    memberId: filters.memberId ? String(filters.memberId) : '',
    status: filters.status || null,
    dynamic: filters.dynamic || {},
  });
}

function getCurrentTaskFilterSignature() {
  const filter = store.getState().board?.filter || {};

  return getTaskFilterSignature({
    searchTitle: filter.title || '',
    memberId: filter.memberId || '',
    status: filter.status || null,
    dynamic: filter.dynamic || {},
  });
}

export async function addTaskOnFirstGroup(board, currentUser) {
  try {
    const accountId = store.getState().accounts.selectedAccount.accountId._id;
    const user = store.getState().auth?.currentUser;

    const taskToAdd = {
      title: "New Task",
      boardId: board?._id,
    };

    const activityData = user?._id ? {
      byMember: { id: user._id, name: user.name },
      field: { action: "create-task", _id: "" },
      to: { value: taskToAdd.title },
      context: { boardId: board?._id },
    } : null;

    const data = await taskService.create(accountId, taskToAdd, activityData);

    if (data?.newTask) dispatchMultipleActions(addTask({ task: data.newTask }));
    if (data?.updatedGroup) dispatchMultipleActions(updateGroup({ group: data.updatedGroup }));

    if (data?.updatedBoard) {
      dispatchMultipleActions(
        setBoard({ board: data.updatedBoard }),
        setFilterBoard({ filteredBoard: data.updatedBoard })
      );
    }

    return data?.newTask || null;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function addTaskToGroupRequest(groupId, accountId, taskData) {
  try {
    const savedTask = await taskService.createTaskInGroup(groupId, accountId, taskData);
    return savedTask;
  } catch (err) {
    console.error("Error adding task to group:", err);
    throw err;
  }
}


export async function addTaskToGroup(groupId, board, taskData = { title: "New Task" }) {
  try {
    const accountId = store.getState().accounts.selectedAccount?.accountId._id;
    const user = store.getState().auth?.currentUser;

    const taskToAdd = {
      ...taskData,
      groupId,
    };

    const activityData = user?._id
      ? {
        byMember: {
          id: user._id,
          name: user.name
        },
        field: {
          action: "create-task",
          _id: ""
        },
        to: {
          value: taskToAdd.title
        }
      }
      : null;

    const data = await taskService.create(accountId, taskToAdd, activityData);

    return data.newTask;

  } catch (err) {
    console.error("Error adding task:", err);
    throw err;
  }
}

export async function createNewTaskBelow(taskId, newTaskData) {
  try {
    const accountId = store.getState().accounts.selectedAccount.accountId._id;
    const user = store.getState().auth?.currentUser;
    const groups = store.getState().groups.groups;

    const targetGroup = groups.find(g => (g.tasks || []).includes(taskId));
    if (!targetGroup) throw new Error("Group not found");

    const activityData = {
      byMember: { id: user._id, name: user.name },
      field: { action: "create-task", _id: "" },
      from: null,
      to: { value: newTaskData.title }
    };

    const res = await taskService.create(
      accountId,
      {
        ...newTaskData,
        groupId: targetGroup._id,
      },
      activityData,
      { insertAfterTaskId: taskId }
    );

    const createdTask = res?.newTask ?? res;
    const updatedGroup = res?.updatedGroup;


    return { task: createdTask, group: updatedGroup };
  } catch (err) {
    console.error("Error creating task below:", err);
    throw err;
  }
}

export async function updateTaskInGroup(
  taskId,
  updatedData,
  isSub,
  idAccount,
  activityLog,
  groupId
  , columnId) {
  try {
    if (isSub) dispatch(setLoading(true));

    const state = store.getState();
    const accountId = state.accounts.selectedAccount?.accountId?._id || idAccount;
    const user = state.auth?.currentUser;

    const patch = (updatedData && typeof updatedData === 'object') ? { ...updatedData } : {};

    delete patch.user;
    delete patch.updatedBy;
    delete patch.timeUpdate;
    delete patch.activities;
    delete patch.accountId;

    function normalizeActivityPrimitive(v) {
      if (v == null) return "";

      const t = typeof v;

      if (t === "string" || t === "number" || t === "boolean") return v;

      if (t === "object") {
        if (Object.prototype.hasOwnProperty.call(v, "value")) return normalizeActivityPrimitive(v.value);
        if (Object.prototype.hasOwnProperty.call(v, "title")) return normalizeActivityPrimitive(v.title);
        if (Object.prototype.hasOwnProperty.call(v, "name")) return normalizeActivityPrimitive(v.name);
        if (Object.prototype.hasOwnProperty.call(v, "label")) return normalizeActivityPrimitive(v.label);
      }

      try {
        return JSON.stringify(v);
      } catch {
        return String(v);
      }
    }

    function normalizeActivityAction(action, fallback) {
      const a = typeof action === "string" ? action.trim() : "";
      if (a) return a;
      const fb = typeof fallback === "string" ? fallback.trim() : "";
      return fb || "";
    }

    const activityData = (activityLog && user?._id)
      ? {
        action: normalizeActivityAction(activityLog.action, ""),
        componentId: activityLog.id || "",
        from: normalizeActivityPrimitive(activityLog.from),
        to: normalizeActivityPrimitive(activityLog.to),
        context: {
          isSub: !!isSub,
          taskId,
          groupId: groupId || patch?.groupId || null,
        },
        byMember: {
          id: user._id,
          name: user.name,
          imgURL: user.imgURL || user.imgUrl || "",
        },
      }
      : null;

    const updatedTask = await taskService.update(accountId, taskId, patch, activityData);

    const finalGroupId = groupId || patch?.groupId || updatedTask?.groupId;

    if (finalGroupId) {
      if (updatedTask?.active === false) {
        dispatchMultipleActions(
          updateTask({ task: updatedTask }),
          removeGroupTask({ groupId: finalGroupId, taskId: updatedTask._id })
        );
      } else {
        dispatchMultipleActions(
          updateTask({ task: updatedTask }),
          updateGroupTask({ groupId: finalGroupId, task: updatedTask })
        );
      }
    } else {
      dispatchMultipleActions(updateTask({ task: updatedTask }));
    }

    return updatedTask;
  } catch (err) {
    console.error("Error updating task:", err);
    throw err;
  } finally {
    dispatch(setLoading(false));
  }
}

export async function pauseStatusTimeTracking(taskId, automationId) {
  try {
    const state = store.getState();
    const accountId = state.accounts.selectedAccount?.accountId?._id;

    const updatedTask = await taskService.pauseStatusTimeTracking(accountId, taskId, automationId);
    const groupId = updatedTask?.groupId;

    if (groupId) {
      dispatchMultipleActions(
        updateTask({ task: updatedTask }),
        updateGroupTask({ groupId, task: updatedTask })
      );
    } else {
      dispatchMultipleActions(updateTask({ task: updatedTask }));
    }

    return updatedTask;
  } catch (err) {
    console.error("Error pausing status time tracking:", err);
    throw err;
  }
}

export async function removeSubTask(taskId, updatedData, parentTask, activityLog) {
  try {
    dispatch(setLoading(true));

    const accountId = store.getState().accounts.selectedAccount.accountId._id;
    const user = store.getState().auth?.currentUser;

    const activity = (user?._id && activityLog?.action)
      ? {
        action: activityLog.action,
        componentId: activityLog.id || "",
        from: activityLog.from ?? null,
        to: activityLog.to ?? null,
        byMember: { id: user._id, name: user.name },
        context: {
          isSub: true,
          subtaskId: taskId,
          parentTaskId: parentTask?._id,
        },
      }
      : null;

    const res = await taskService.removeSubtask(
      accountId,
      parentTask._id,
      taskId,
      { activity }
    );

    const parentTaskUpdated =
      res?.parentTask ||
      res?.parent ||
      res?.updatedParentTask ||
      null;

    const deletedSubtaskId =
      res?.deletedSubtaskId ||
      res?.deletedId ||
      taskId;

    if (parentTaskUpdated) {
      dispatchMultipleActions(updateTask({ task: parentTaskUpdated }));
    } else {
      const fallbackParent = {
        ...parentTask,
        subtasks: (parentTask.subtasks || []).filter((id) => String(id) !== String(taskId)),
        timeUpdate: Date.now(),
      };
      dispatchMultipleActions(updateTask({ task: fallbackParent }));
    }

    return { parentTask: parentTaskUpdated, deletedSubtaskId };
  } catch (err) {
    throw err;
  } finally {
    dispatch(setLoading(false));
  }
}

export async function addSubTask(taskId, subtaskTitle) {
  try {
    const accountId = store.getState().accounts.selectedAccount.accountId._id;
    const user = store.getState().auth?.currentUser;

    const title = String(subtaskTitle ?? "").trim();

    const res = await taskService.addSubtask(accountId, taskId, {
      title,
      user: {
        id: user?._id || "",
        name: user?.name || "",
        imgURL: user?.imgURL || user?.imgUrl || ""
      }
    });

    if (res?.parentTask) {
      dispatchMultipleActions(updateTask({ task: res.parentTask }));
    }

    if (res?.subtask) {
      dispatchMultipleActions(addTask({ task: res.subtask }));
    }

    return res;
  } catch (err) {
    throw err;
  }
}

export async function duplicateTask(groupId, taskId, tableFiles, commentsAndReplies) {
  try {
    const accountId = store.getState().accounts.selectedAccount.accountId._id;
    const user = store.getState().auth?.currentUser;

    const response = await taskService.duplicateTask(taskId, accountId, { tableFiles, commentsAndReplies, user });

    const duplicatedTask = response?.task || response?.newTask || response?.duplicatedTask;
    const duplicatedSubtasks = response?.subtasks || [];
    const updatedGroup = response?.group || response?.updatedGroup || null;

    if (duplicatedTask) dispatchMultipleActions(addTask({ task: duplicatedTask }));

    if (Array.isArray(duplicatedSubtasks) && duplicatedSubtasks.length) {
      for (const st of duplicatedSubtasks) {
        dispatchMultipleActions(addTask({ task: st }));
      }
    }

    if (updatedGroup) {
      dispatchMultipleActions(updateGroup({ group: updatedGroup }));
    }

    return duplicatedTask;
  } catch (err) {
    console.error("Error duplicating task:", err);
    throw err;
  }
}

export async function duplicateSubTask(taskId, parentTask, tableFiles, commentsAndReplies) {
  try {
    const accountId = store.getState().accounts.selectedAccount.accountId._id;
    const user = store.getState().auth?.currentUser;

    const response = await taskService.duplicateTask(taskId, accountId, { tableFiles, commentsAndReplies, user });

    const subtask = response?.subtask;
    const parentTaskUpdated = response?.parentTask || response?.parent || response?.updatedParentTask;

    if (!subtask) {
      throw new Error("Resposta inválida ao duplicar subtask.");
    }

    dispatchMultipleActions(addTask({ task: subtask }));

    if (parentTaskUpdated) {
      dispatchMultipleActions(updateTask({ task: parentTaskUpdated }));
    } else {
      dispatchMultipleActions(updateTask({ task: { ...parentTask, timeUpdate: Date.now() } }));
    }

    return subtask;
  } catch (err) {
    console.error("Erro ao duplicar subtask:", err);
    throw err;
  }
}

export async function getSubTasksByParentId(parentId) {
  try {
    const subtasks = await taskService.getSubTasksByParentId(parentId);
    return subtasks;
  } catch (err) {
    console.error("Error updating task:", err);
    throw err;
  }
}

//TODO: FORMATO AINDA INVALIDO DEVE SER CRIDO 3 END POITNS NOVOS
//TODO: POST /task/:accountId/:taskId/comments - PUT /task/:accountId/:taskId/comments/:commentId - DELETE /task/:accountId/:taskId/comments/:commentId
export async function createComment(taskId, task, comment) {
  try {
    const accountId = store.getState().accounts.selectedAccount.accountId._id;
    const user = store.getState().auth?.currentUser;

    const safeComment = {
      txt: comment?.txt ?? "",
      files: Array.isArray(comment?.files) ? comment.files : [],
      answers: Array.isArray(comment?.answers) ? comment.answers : [],
      likes: comment?.likes || { count: 0, byUsers: [] },
      byMember: comment?.byMember
        ? {
          _id: comment.byMember._id || comment.byMember.id || "",
          name: comment.byMember.name || "",
          email: comment.byMember.email || ""
        }
        : null
    };

    if (safeComment && safeComment._id) delete safeComment._id;

    const payload = {
      comment: safeComment,
      user: user
        ? { _id: user._id, name: user.name, imgURL: user.imgURL || user.imgUrl || "" }
        : null
    };

    const res = await taskService.createComment(accountId, taskId, payload);

    const updatedTask = res?.task || res?.updatedTask || res?.result || res;
    const parentTaskUpdated = res?.parentTask || null;

    if (updatedTask) dispatchMultipleActions(updateTask({ task: updatedTask }));
    if (parentTaskUpdated) dispatchMultipleActions(updateTask({ task: parentTaskUpdated }));

    return updatedTask;
  } catch (err) {
    console.error("Error creating comment:", err);
    throw err;
  }
}

export async function deleteComment(taskId, task, comment) {
  try {
    const accountId = store.getState().accounts.selectedAccount.accountId._id;
    const user = store.getState().auth?.currentUser;

    const commentId = comment?._id;
    if (!commentId) throw new Error("commentId inválido");

    const payload = {
      user: user
        ? { _id: user._id, name: user.name, imgURL: user.imgURL || user.imgUrl || "" }
        : null
    };

    const res = await taskService.deleteComment(accountId, taskId, commentId, payload);

    const updatedTask = res?.task || res?.updatedTask || res?.result || res;
    const parentTaskUpdated = res?.parentTask || null;

    if (updatedTask) dispatchMultipleActions(updateTask({ task: updatedTask }));
    if (parentTaskUpdated) dispatchMultipleActions(updateTask({ task: parentTaskUpdated }));

    return updatedTask;
  } catch (err) {
    console.error("Error deleting comment:", err);
    throw err;
  }
}

export async function updateComment(taskId, task, comment, oldComment) {
  try {
    const accountId = store.getState().accounts.selectedAccount.accountId._id;
    const user = store.getState().auth?.currentUser;

    const commentId = comment?._id;
    if (!commentId) throw new Error("commentId inválido");

    const safePatch = {
      txt: comment?.txt ?? "",
      files: Array.isArray(comment?.files) ? comment.files : [],
      answers: Array.isArray(comment?.answers) ? comment.answers : [],
      likes: comment?.likes || { count: 0, byUsers: [] }
    };

    const payload = {
      patch: safePatch,
      user: user
        ? { _id: user._id, name: user.name, imgURL: user.imgURL || user.imgUrl || "" }
        : null,
      meta: {
        oldTxt: oldComment ?? null
      }
    };

    const res = await taskService.updateComment(accountId, taskId, commentId, payload);

    const updatedTask = res?.task || res?.updatedTask || res?.result || res;
    const parentTaskUpdated = res?.parentTask || null;

    if (updatedTask) dispatchMultipleActions(updateTask({ task: updatedTask }));
    if (parentTaskUpdated) dispatchMultipleActions(updateTask({ task: parentTaskUpdated }));

    return updatedTask;
  } catch (err) {
    console.error("Error updating comment:", err);
    throw err;
  }
}

export async function createImage(obj) {
  try {
    const accountId = store.getState().accounts.selectedAccount.accountId._id;
    const { data } = await imageService.addImage(obj, accountId);
    return data
  } catch (err) {
    throw err;
  }
}

export async function getImageByUUID(uuid) {
  try {
    const data = await imageService.getImageByUUID(uuid);
    return data
  } catch (err) {
    throw err;
  }
}

export async function deleteImageByUUID(uuid) {
  const data = await imageService.deleteImageByUUID(uuid);
  return data
}

export async function createNewSubTaskBelow(parentTaskId, afterSubtaskId, newSubtaskData = {}) {
  try {
    const accountId = store.getState().accounts.selectedAccount.accountId._id;
    const user = store.getState().auth?.currentUser;

    const payload = {
      ...newSubtaskData,
      title: String(newSubtaskData?.title ?? "").trim(),
      user: user
        ? { id: user._id, name: user.name, imgURL: user.imgURL || user.imgUrl || "" }
        : undefined,
    };

    const res = await taskService.addSubtask(
      accountId,
      parentTaskId,
      payload,
      { insertAfterSubtaskId: afterSubtaskId }
    );

    const parentTask = res?.parentTask || res?.parent || res?.updatedParentTask || null;
    const subtask = res?.subtask || res?.task || null;

    if (parentTask) dispatchMultipleActions(updateTask({ task: parentTask }));
    if (subtask) dispatchMultipleActions(addTask({ task: subtask }));

    return subtask;
  } catch (err) {
    console.error("Error creating subtask below:", err);
    throw err;
  }
}

export function toggleSubTask(isSubtask) {
  dispatchMultipleActions(setIsSubtask({ isSubtask }));
}

export function setCurrSubtask(subtask) {
  dispatchMultipleActions(setCurrentSubtask({ currentSubtask: subtask }));
}

export function refreshSubtasks(parentId, subtasks) {
  dispatchMultipleActions(setSubtasks({ parentId, subtasks }));
}

export async function updateBoardStatusLabels(componentId, labels) {
  const returned = await boardService.updateLabel(componentId, labels);

  const board =
    returned?.board ||
    returned?.updatedBoard ||
    returned;

  if (board) {
    dispatchMultipleActions(
      setBoard({ board }),
      setFilterBoard({ filteredBoard: board })
    );
  }

  return returned;
}

export async function updateTaskStatusLabels(taskId, componentId, labels) {
  const accountId = store.getState().accounts.selectedAccount.accountId._id;

  const res = await taskService.updateTaskLabels(accountId, taskId, {
    componentId,
    labels: labels.labels,
  });

  const updatedTask = res?.task || res;
  if (updatedTask) dispatchMultipleActions(updateTask({ task: updatedTask }));

  return updatedTask;
}

export async function updateLabel(id, labels, isSubtasks, componentId = null) {
  try {
    if (!isSubtasks) {
      return await updateBoardStatusLabels(id, labels);
    }

    if (!componentId) {
      throw new Error("componentId é obrigatório para atualizar labels dentro da task.");
    }

    return await updateTaskStatusLabels(id, componentId, labels);
  } catch (err) {
    console.error("Error updating label:", err);
    throw err;
  }
}

export async function updateCmpLabel(taskId, cmp, isResize) {
  try {
    if (!isResize) dispatch(setLoading(true));

    const accountId = store.getState().accounts.selectedAccount.accountId._id;

    const res = await taskService.updateTaskCmpLabel(accountId, taskId, cmp);
    const updatedTask = res?.task || res;

    if (updatedTask) {
      dispatchMultipleActions(updateTask({ task: updatedTask }));
    }

    return updatedTask;
  } catch (err) {
    console.error("Error updating label:", err);
    throw err;
  } finally {
    dispatch(setLoading(false));
  }
}

export async function deleteCmpLabel(taskId, cmpId) {
  try {
    dispatch(setLoading(true));

    const accountId = store.getState().accounts.selectedAccount.accountId._id;

    const res = await taskService.deleteTaskCmpLabel(accountId, taskId, cmpId);
    const updatedTask = res?.task || res;

    if (updatedTask) {
      dispatchMultipleActions(updateTask({ task: updatedTask }));
    }

    return updatedTask;
  } catch (err) {
    console.error("Error deleting cmp label:", err);
    throw err;
  } finally {
    dispatch(setLoading(false));
  }
}

export async function handleDragEndSubtask(result, task) {
  dispatch(setLoading(true));

  if (!result.destination) {
    dispatch(setLoading(false));
    return;
  }
  const accountId = store.getState().accounts.selectedAccount.accountId._id;
  const { source, destination, type } = result;

  if (type === "subtask") {
    try {
      const reorderedSubtasks = Array.from(task.subtasks);
      const [movedSubtask] = reorderedSubtasks.splice(source.index, 1);
      reorderedSubtasks.splice(destination.index, 0, movedSubtask);

      const updatedParentTask = await taskService.update(accountId, task._id, { subtasks: reorderedSubtasks });

      dispatchMultipleActions(
        updateTask({ task: updatedParentTask })
      );
    } catch (err) {
      console.error("Error updating label:", err);
    } finally {
      dispatch(setLoading(false));
    }
  }

  if (type === "title") {
    try {
      const updatedTitles = [...task.cmpsOrder];
      const [draggedItem] = updatedTitles.splice(result.source.index, 1);
      updatedTitles.splice(result.destination.index, 0, draggedItem);

      const order = updatedTitles.map(c => c.id);

      const updatedParentTask = await taskService.reorderTaskColumns(accountId, task._id, { order });

      dispatchMultipleActions(
        updateTask({ task: updatedParentTask })
      );
    } catch (err) {
      console.error("Error updating label:", err);
    } finally {
      dispatch(setLoading(false));
    }
  }
};

async function blobUrlToFile(blobUrl, fileName, mimeType) {
  const response = await fetch(blobUrl);
  const blob = await response.blob();
  return new File([blob], fileName, { type: mimeType });
}

export async function uploadCommentBlobs(fileOps, boardId, groupId, taskId) {
  const uploadedFileInfos = [];

  for (const op of fileOps) {
    try {
      const fileInfo = op.insert.file;
      const fileBlob = await blobUrlToFile(fileInfo.url, fileInfo.name, fileInfo.type);

      const accountId = store.getState().accounts.selectedAccount?.accountId?._id;

      const response = await uploadService.uploadCommentFile({
        boardId,
        groupId,
        taskId,
        file: fileBlob,
        accountId
      });

      const ext = fileInfo.name.split('.').pop();
      const backendBaseUrl = await utilService.getBackendBaseUrl();
      const realUrl = `${backendBaseUrl}/uploads/${accountId}/files/${response.uuid}.${ext}`;

      op.insert.file.uuid = response.uuid;
      op.insert.file.url = realUrl;
      op.insert.file.name = response.originalName;

      uploadedFileInfos.push({
        uuid: response.uuid
      });
    } catch (err) {
      console.error(`Erro ao processar arquivo`, err);
      throw err;
    }
  }

  return uploadedFileInfos;
}


export async function addLikeCount(
  taskId,
  task,
  commentOrAnswerId,
  increment = 1,
  parentCommentId = null
) {
  try {
    const accountId = store.getState().accounts.selectedAccount.accountId._id;
    const user = store.getState().auth.currentUser;

    const payload = {
      increment,
      parentCommentId,
      targetId: commentOrAnswerId,
      userId: user?._id,
      user: {
        _id: user?._id,
        name: user?.name,
        imgURL: user?.imgURL || user?.imgUrl || ""
      }
    };

    const res = await taskService.toggleLike(accountId, taskId, payload);

    const updatedTask = res?.task || res;

    if (updatedTask) {
      dispatch(updateTask({ task: updatedTask }));
    }

    return updatedTask;
  } catch (err) {
    console.error("Erro ao atualizar likeCount:", err);
    throw err;
  }
}

export async function createAnswer(taskId, task, commentId, answer) {
  try {
    const accountId = store.getState().accounts.selectedAccount.accountId._id;
    const user = store.getState().auth?.currentUser;

    const payload = {
      txt: answer?.txt ?? "",
      files: Array.isArray(answer?.files) ? answer.files : [],
      user: {
        _id: user?._id,
        name: user?.name,
        imgURL: user?.imgURL || user?.imgUrl || ""
      }
    };

    const res = await taskService.createAnswer(accountId, taskId, commentId, payload);

    const updatedTask = res?.task || res;
    const parentTaskUpdated = res?.parentTask || null;

    if (updatedTask) dispatch(updateTask({ task: updatedTask }));
    if (parentTaskUpdated) dispatch(updateTask({ task: parentTaskUpdated }));

    return updatedTask;
  } catch (err) {
    console.error("Error creating answer:", err);
    throw err;
  }
}

export async function deleteAnswer(taskId, task, commentId, answerId) {
  try {
    const accountId = store.getState().accounts.selectedAccount.accountId._id;
    const user = store.getState().auth?.currentUser;

    const payload = {
      user: user
        ? { _id: user._id, name: user.name, imgURL: user.imgURL || user.imgUrl || "" }
        : null
    };

    const res = await taskService.deleteAnswer(accountId, taskId, commentId, answerId, payload);

    const updatedTask = res?.task || res;
    const parentTaskUpdated = res?.parentTask || null;

    if (updatedTask) dispatch(updateTask({ task: updatedTask }));
    if (parentTaskUpdated) dispatch(updateTask({ task: parentTaskUpdated }));

    return updatedTask;
  } catch (err) {
    console.error("Error deleting answer:", err);
    throw err;
  }
}

export async function updateAnswer(taskId, task, commentId, updatedAnswer) {
  try {
    const accountId = store.getState().accounts.selectedAccount.accountId._id;
    const user = store.getState().auth?.currentUser;

    const answerId = updatedAnswer?._id;
    if (!commentId || !answerId) throw new Error("commentId/answerId inválido");

    const payload = {
      patch: {
        txt: updatedAnswer?.txt ?? "",
        files: Array.isArray(updatedAnswer?.files) ? updatedAnswer.files : []
      },
      user: user
        ? { _id: user._id, name: user.name, imgURL: user.imgURL || user.imgUrl || "" }
        : null
    };

    const res = await taskService.updateAnswer(accountId, taskId, commentId, answerId, payload);

    const updatedTask = res?.task || res;
    const parentTaskUpdated = res?.parentTask || null;

    if (updatedTask) dispatch(updateTask({ task: updatedTask }));
    if (parentTaskUpdated) dispatch(updateTask({ task: parentTaskUpdated }));

    return updatedTask;
  } catch (err) {
    console.error("Error updating answer:", err);
    throw err;
  }
}

export async function getTasksByGroupId(groupId, filters) {
  try {
    const tasks = await taskService.getTasksByGroup(groupId, filters);

    return tasks;
  } catch (err) {
    console.error("Error loading task:", err);
    dispatch(setLoading(false))
    throw err;
  } finally {
    dispatch(setLoading(false))
  }
}

export const loadTasksByGroupId = (group, filters = {}) => async (dispatch) => {
  if (!group || !group._id) {
    console.warn("Group inválido ao carregar tarefas:", group);
    return;
  }

  const groupId = group._id || group;
  const requestFilterSignature = getTaskFilterSignature(filters);

  try {
    const { tasks, allTaskIds } = await getTasksByGroupId(groupId, filters);

    if (requestFilterSignature !== getCurrentTaskFilterSignature()) {
      return;
    }

    const result = syncArrays(group.tasks, allTaskIds);

    if (result.changed === true) {
      const updatedGroup = { ...group, tasks: result.array }

      dispatch(updateGroup({ group: updatedGroup }));
    }

    dispatch(setGroupTasks({ groupId, tasks }));
  } catch (err) {
    console.error('Erro ao carregar tasks para o grupo', groupId, err);
  }
};

export async function deleteCommentDraft(key) {
  try {
    dispatch(clearCommentDraft({ key }));
  } catch (e) {
    console.error('Erro ao deletar draft:', e);
  }
}

export async function saveCommentDraft(key, content) {
  try {
    dispatch(setCommentDraft({ key, content }));
  } catch (e) {
    console.error('Erro ao salvar draft:', e);
  }
}

export const clearTasksByGroupId = (groupId) => (dispatch) => {
  dispatch(clearGroupTasks({ groupId }));
};



export async function addTaskActivity(taskId, activityData, opts = {}) {
  try {
    const accountId = store.getState().accounts.selectedAccount?.accountId?._id;

    const updatedTask = await taskService.addTaskActivity(accountId, taskId, activityData);

    if (!opts.silent) {
      dispatch(updateTask({ task: updatedTask.task }));
    }

    return updatedTask;
  } catch (err) {
    console.error("Erro ao adicionar atividade na task:", err);
    throw err;
  }
}

export function getActivityDescription(activity) {
  const action = activity?.field?.action?.toLowerCase();
  if (!action) return 'Alterou a Coluna';

  if (action.includes('comment-add')) return `Adicionou um comentário`;
  if (action.includes('comment-remove')) return `Removeu um comentário`;
  if (action.includes('move-task')) return `Movimentou a task`;

  if (action.includes('comment-reply-edit')) return `Editou uma resposta`;
  if (action.includes('comment-reply-remove')) return `Removeu uma resposta`;
  if (action.includes('comment-reply')) return `Respondeu um comentário`;
  if (action.includes('comment-edit')) return `Editou um comentário`;
  if (action.includes('comment-reply-edit')) return `Editou uma resposta`;
  if (action.includes('files-add')) return `Adicionou arquivos a Coluna`;
  if (action.includes('files-remove')) return `Removeu um arquivo na Coluna`;
  if (action.includes('member-add')) return `Adicionou um membro a Coluna`;
  if (action.includes('member-remove')) return `Removeu um membro a Coluna`;
  if (action.includes('create-task')) return `Criou a task`;
  if (action.includes('create-subtask')) return `Criou a subtask`;
  if (action.includes('change-task')) return `Mudou a task`;
  if (action.includes('duplicate-task')) return `Duplicou a task`;
  if (action.includes('duplicate-subtask')) return `Duplicou a Subtask`;
  if (action.includes('remove-subtask')) return `Removeu a Subtask`;
  if (action.includes('remove-task')) return `Removeu a Task`;
  if (action.includes('restore-task')) return `Restaurou a Task`;
  if (action.includes('restore-subtask')) return `Restaurou a Subtask`;
  if (action.includes('change-subtask')) return `Mudou a subtask`;
  if (action.includes('board-movetasks')) return `Moveu a task da Board`;

  if (action.includes('automation-notify')) return `Notificou os E-mails`;

  return `Alterou a Coluna`;
}

export async function moveTasksAnotherBoard(tasks, boardId, groupId, oldBoardId) {
  try {
    const state = store.getState();

    const movingTaskIds = (Array.isArray(tasks) ? tasks : [])
      .map(t => (typeof t === "string" ? t : t?._id))
      .filter(Boolean)
      .map(String);

    let accountId =
      state?.accounts?.selectedAccount?.accountId?._id ||
      state?.accounts?.selectedAccount?.accountId ||
      state?.accounts?.selectedAccount?._id ||
      state?.auth?.currentUser?.accountId ||
      state?.auth?.currentUser?.accounts?.[0]?.accountId?._id ||
      state?.auth?.currentUser?.accounts?.[0]?.accountId ||
      null;

    accountId = accountId ? String(accountId) : "";

    if (accountId && movingTaskIds.includes(accountId)) {
      const alt =
        state?.accounts?.selectedAccount?.accountId?._id ||
        state?.accounts?.selectedAccount?.accountId ||
        state?.accounts?.selectedAccount?._id ||
        "";

      accountId = alt ? String(alt) : "";
    }

    if (!accountId) {
      console.error("[moveTasksAnotherBoard] selectedAccount=", state?.accounts?.selectedAccount);
      throw new Error("moveTasksAnotherBoard: accountId não encontrado no store.");
    }

    const user = state.auth?.currentUser;

    if (!boardId) {
      throw new Error("moveTasksAnotherBoard: boardId de destino é obrigatório.");
    }

    if (!groupId) {
      throw new Error("moveTasksAnotherBoard: groupId de destino é obrigatório.");
    }

    const currentBoardId =
      state.board?.board?._id ||
      state.board?.selectedBoard?._id ||
      state.board?.selectedBoardId ||
      null;

    const finalOldBoardId = String(oldBoardId || currentBoardId || "");
    if (!finalOldBoardId) {
      throw new Error("moveTasksAnotherBoard: oldBoardId é obrigatório (não consegui inferir pela board atual).");
    }

    const res = await taskService.moveTasksAnotherBoard(
      accountId,
      tasks,
      boardId,
      groupId,
      finalOldBoardId,
      user
    );

    const updatedTasks = res?.updatedTasks || res?.tasks || res || [];
    const movedFromByTaskId = res?.movedFromByTaskId || {};
    const toGroupId = String(res?.toGroupId || groupId || "");
    const affectedGroupIds = new Set();

    for (const task of updatedTasks) {
      if (!task?._id) continue;

      const destGroupId = String(task.groupId || toGroupId || "");
      if (!destGroupId) continue;

      dispatch(updateGroupTask({ groupId: destGroupId, task }));
      affectedGroupIds.add(destGroupId);
    }

    for (const task of updatedTasks) {
      const taskId = String(task?._id || "");
      if (!taskId) continue;

      const fromGroupId = String(movedFromByTaskId?.[taskId] || "");
      const destGroupId = String(task.groupId || toGroupId || "");

      if (fromGroupId) {
        affectedGroupIds.add(fromGroupId);
      }

      if (fromGroupId && destGroupId && fromGroupId !== destGroupId) {
        dispatch(removeGroupTask({ groupId: fromGroupId, taskId }));
      }
    }

    affectedGroupIds.forEach((affectedGroupId) => {
      window.dispatchEvent(
        new CustomEvent("refresh-group-statistics", {
          detail: { groupId: affectedGroupId }
        })
      );
    });

    affectedGroupIds.forEach((affectedGroupId) => {
      socket.emit("fetchStatistics", { groupId: affectedGroupId });
    });

    return updatedTasks;
  } catch (err) {
    console.error("Erro ao mover tasks para outra board:", err);
    throw err;
  }
}

export async function archivedSubtaskOfTask(taskId) {
  try {
    const accountId = store.getState().accounts.selectedAccount?.accountId?._id;
    const user = store.getState().auth?.currentUser;
    return await taskService.archivedSubtasks(accountId, taskId, user);
  } catch (err) {
    console.error("Erro ao arquivar subtasks da task arquivada:", err);
    throw err;
  }
}

export async function deleteFilesCommentAndReply(accountId, uuids) {
  try {
    await deleteCommentReplyFiles(accountId, uuids);
  } catch (err) {
    console.error("Erro ao deletar files comment and reply:", err);
    throw err;
  }
}

export async function getTaskByFilteredAnalisys(accountId, userId, opts) {
  try {
    const data = await taskService.getTaskByAnalisys(accountId, userId, opts);
    return data;
  } catch (err) {
    console.error("Error ao buscar task para análise:", err);
    throw err;
  }
}

export async function getUserTaskMentions(accountId, userId, opts) {
  try {
    const data = await taskService.getTaskMention(accountId, userId, opts);
    return data;
  } catch (err) {
    console.error("Error ao buscar task de menção para usuário:", err);
    throw err;
  }
}

export async function getTaskById(taskId) {
  try {
    const data = await taskService.getById(taskId);
    return data;
  } catch (err) {
    console.error("Error ao buscar task de menção para usuário:", err);
    throw err;
  }
}

export async function createColumnOnTask(taskId, payload) {
  try {
    const accountId = store.getState().accounts.selectedAccount.accountId._id;

    const res = await taskService.createTaskColumn(accountId, taskId, payload);

    // Normaliza retorno: pode vir task direta OU wrapper { task }
    const updatedTask = res?.task || res;

    console.log('[createColumnOnTask] updatedTask._id:', updatedTask?._id);
    console.log('[createColumnOnTask] updatedTask.cmpsOrder:', updatedTask?.cmpsOrder);

    dispatchMultipleActions(
      updateTask({ task: updatedTask })
    );

    return updatedTask;
  } catch (err) {
    console.error("Error creating column on task:", err);
    throw err;
  }
}

export async function reorderColumnsOnTask(taskId, order) {
  try {
    const accountId = store.getState().accounts.selectedAccount.accountId._id;

    const updatedTask = await taskService.reorderTaskColumns(accountId, taskId, { order });

    dispatchMultipleActions(
      updateTask({ task: updatedTask })
    );

    return updatedTask;
  } catch (err) {
    console.error("Error reordering columns:", err);
    throw err;
  }
}

// BPMN (por task) - carregar XML sob demanda
export async function loadTaskBpmnDiagram(accountId, taskId) {
  try {
    const data = await taskService.getTaskBpmnDiagram(accountId, taskId);
    return data;
  } catch (err) {
    console.error("Erro ao carregar BPMN da task:", err);
    throw err;
  }
}

// BPMN (por task) - salvar XML sob demanda (upsert no backend)
export async function saveTaskBpmnDiagram(accountId, taskId, xml) {
  try {
    const safeXml = typeof xml === "string" ? xml : "";
    const data = await taskService.saveTaskBpmnDiagram(accountId, taskId, safeXml);

    const state = store.getState();

    const existingTask =
      state?.taskModule?.taskById?.[taskId] ||
      (Array.isArray(state?.taskModule?.tasks)
        ? state.taskModule.tasks.find((t) => t?._id === taskId)
        : null);

    if (!existingTask) {
      console.warn("Task não encontrada no store para merge do BPMN:", taskId);
      return data;
    }

    const mergedTask = {
      ...existingTask,
      diagram: {
        ...(existingTask.diagram || {}),
        hasBpmn: safeXml.trim().length > 0,
        bpmnVersion: data?.version || 0,
        bpmnUpdatedAt: data?.updatedAt || new Date(),
      },
    };

    dispatchMultipleActions(updateTask({ task: mergedTask }));

    return data;
  } catch (err) {
    console.error("Erro ao salvar BPMN da task:", err);
    throw err;
  }
}

function syncArrays(array1, array2) {
  const set2 = new Set(array2);
  const kept = array1.filter(id => set2.has(id));

  const setKept = new Set(kept);
  const added = array2.filter(id => !setKept.has(id));

  const final = [...kept, ...added];

  const changed = final.length !== array1.length ||
    final.some((id, idx) => id !== array1[idx]);

  return {
    array: final,
    changed
  };
}

export async function listTaskDocuments(accountId, taskId, paramsOrType = "bpmn") {
  const params =
    typeof paramsOrType === "string"
      ? { type: paramsOrType }
      : (paramsOrType || {});

  const data = await taskService.listTaskDocuments(accountId, taskId, params);

  if (Array.isArray(data)) return data;

  const list =
    data?.documents ||
    data?.taskDocuments ||
    data?.items ||
    data?.data ||
    data?.rows ||
    data?.result;

  return Array.isArray(list) ? list : [];
}

export async function createBpmnDocument(accountId, taskId, inputTitle) {
  const title =
    (typeof inputTitle === "string" ? inputTitle : inputTitle?.title) || "";

  const payload = {
    type: "bpmn",
    title: title.trim() || "Diagrama",
    content: { text: "" }
  };

  const data = await taskService.createTaskDocument(accountId, taskId, payload);

  return data?.document || data?.taskDocument || data;
}

export async function loadTaskBpmnDocument(accountId, taskId, documentId) {
  const data = await taskService.getTaskDocumentById(accountId, taskId, documentId);
  return data?.document || data?.taskDocument || data;
}

export async function saveTaskBpmnDocument(accountId, taskId, documentId, xml, title) {
  const safeXml = typeof xml === "string" ? xml : "";
  const payload = {
    xml: safeXml,
    content: { text: safeXml },
    ...(title ? { title } : {})
  };

  const data = await taskService.updateTaskDocumentById(accountId, taskId, documentId, payload);
  return data?.document || data?.taskDocument || data;
}

// CRIA um documento genérico
export async function createTaskDocument(accountId, taskId, payload) {
  try {
    return await taskService.createTaskDocument(accountId, taskId, payload);
  } catch (err) {
    console.error("[createTaskDocument] failed:", err);
    throw err;
  }
}

// REMOVE
export async function removeTaskDocument(accountId, taskId, documentId) {
  try {
    return await taskService.deleteTaskDocumentById(accountId, taskId, documentId);
  } catch (err) {
    console.error("[removeTaskDocument] failed:", err);
    throw err;
  }
}
